import { useEffect, useState } from 'react';
import { zeitText } from '../lib/format';
import type { Timer } from '../lib/types';

/**
 * Der Timer über dem Board.
 *
 * In der letzten Minute wird die Anzeige rot, nach Ablauf steht dort „ZEIT ABGELAUFEN“.
 *
 * Gezählt wird hier nur die Anzeige. Die Wahrheit über die Zeit liegt beim Server, und er schickt
 * sie bei jeder Änderung mit. Dazwischen würde die Anzeige stehenbleiben – deshalb läuft sie
 * zwischen zwei Ständen lokal weiter und wird von jedem eintreffenden Stand wieder geradegezogen.
 * Dadurch ist die Anzeige flüssig, ohne dass der Client eigene Entscheidungen über die Spielzeit
 * trifft: Ob noch geklickt werden darf, bestimmt weiterhin ausschließlich der Server.
 */
export function Zeitanzeige({ timer }: { timer: Timer }) {
  const angezeigt = useWeiterlaufend(timer);
  const [jetzt, setJetzt] = useState(Date.now());
  useEffect(() => {
    if (!timer.preparationEndsAt || timer.phase !== 'IDLE') return;
    const uhr = setInterval(() => setJetzt(Date.now()), 250);
    return () => clearInterval(uhr);
  }, [timer.preparationEndsAt, timer.phase]);
  if (timer.phase === 'IDLE' && timer.preparationEndsAt) {
    const rest = Math.max(0, Math.ceil((Date.parse(timer.preparationEndsAt) - jetzt) / 1000));
    return <div className="timer"><span className="timer-value">{`Vorbereitung ${String(Math.floor(rest / 60)).padStart(2, '0')}:${String(rest % 60).padStart(2, '0')}`}</span><span className="timer-note">{rest === 0 ? 'LOS!' : 'Macht euch bereit!'}</span></div>;
  }

  const klassen = [
    'timer',
    angezeigt.finalMinute ? 'final' : '',
    angezeigt.phase === 'EXPIRED' ? 'expired' : '',
    angezeigt.phase === 'PAUSED' ? 'paused' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={klassen}>
      <span className="timer-value">{zeitText(angezeigt)}</span>
      {angezeigt.phase === 'PAUSED' && <span className="timer-note">pausiert</span>}
      {angezeigt.phase === 'IDLE' && <span className="timer-note">bereit</span>}
      {angezeigt.phase === 'ENDED' && <span className="timer-note">Runde beendet</span>}
    </div>
  );
}

/** Zählt die Sekunden zwischen zwei Ständen herunter – aber nur, solange die Runde läuft. */
function useWeiterlaufend(timer: Timer): Timer {
  const [abgelaufen, setAbgelaufen] = useState(0);

  useEffect(() => {
    setAbgelaufen(0);
    if (timer.phase !== 'RUNNING') return;
    const uhr = setInterval(() => setAbgelaufen((bisher) => bisher + 1), 1000);
    return () => clearInterval(uhr);
  }, [timer]);

  if (timer.phase !== 'RUNNING' || abgelaufen === 0) return timer;

  const rest = Math.max(0, timer.remainingSeconds - abgelaufen);
  return {
    ...timer,
    remainingSeconds: rest,
    // Rot wird die Anzeige in der letzten Minute. Dass die Runde wirklich vorbei ist, sagt
    // weiterhin der Server – hier bleibt es bei 00:00 stehen, bis sein Stand eintrifft.
    finalMinute: timer.finalMinute || rest <= 60,
  };
}
